import { useState, useEffect } from "react";

import { Button } from "./component.js";
import { ChevronLeft, ChevronRight } from "../../files/icons/index.js";
import { DateToFormat } from "../../utils/datetime.js";

// CheckinModal lets an admin record (or clear) a patient's Monthly Check-In for the
// viewed billing month. The selected date is constrained to that month and becomes the
// claim's date of service.
//
// When onPrevMonth / onNextMonth are provided the header renders a month navigator so the
// user can record check-ins for months other than the current one.
function CheckinModal({ claim, periodStart, periodEnd, periodLabel, submitting, onClose, onConfirm, onClear, onPrevMonth, onNextMonth }) {
  const min = DateToFormat(periodStart, "YYYY-MM-DD");
  const max = DateToFormat(periodEnd, "YYYY-MM-DD");

  // Default to today when it falls within the viewed month, otherwise the last day of the month.
  const today = DateToFormat(new Date(), "YYYY-MM-DD");
  const defaultDate = today >= min && today <= max ? today : max;

  const [date, setDate] = useState(claim.monthlyCheckinDate || defaultDate);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Reset the date picker when the period changes (month navigation) or when the
  // existing check-in date for the newly selected month changes.
  useEffect(() => {
    setDate(claim.monthlyCheckinDate || defaultDate);
  }, [min, max, claim.monthlyCheckinDate]);

  const valid = date >= min && date <= max;

  const showMonthNav = onPrevMonth || onNextMonth;

  return (
    <div
      className="fixed inset-0 z-2000 flex items-center justify-center bg-[#80808094]"
      onClick={submitting ? undefined : onClose}
    >
      <div className="w-full max-w-md px-8">
        <div className="card shadowmore" onClick={(e) => e.stopPropagation()}>
          <div className="card-header">
            <span className="typo-header-3xLarge">Monthly Check-In</span>
          </div>
          <div className="card-content">
            <div className="d-flex align-items-center mb-3">
              <div className="typo-normal-150 text-muted grow">
                {claim.firstName} {claim.lastName}
              </div>
              {showMonthNav ? (
                <div className="d-flex align-items-center gap-1">
                  <div className={`item rect${submitting ? " disabled" : ""}`} onClick={submitting ? undefined : onPrevMonth}>
                    <ChevronLeft height={14} width={14} color="grey" />
                  </div>
                  <span className="typo-normal-150 text-muted mx-1">{periodLabel}</span>
                  <div className={`item rect${submitting ? " disabled" : ""}`} onClick={submitting ? undefined : onNextMonth}>
                    <ChevronRight height={14} width={14} color="grey" />
                  </div>
                </div>
              ) : (
                <span className="typo-normal-150 text-muted">{periodLabel}</span>
              )}
            </div>

            <div className="typo-strong-125 mb-1">Check-in date</div>
            <input
              type="date"
              value={date}
              min={min}
              max={max}
              disabled={submitting}
              onChange={(e) => setDate(e.target.value)}
              className="input md typo-body-medium w-full"
            />
            {!valid && (
              <div className="typo-normal-125 text-red-600 mt-1">
                Select a date within {periodLabel}.
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-4">
              <div>
                {claim.monthlyCheckinComplete && (
                  <Button
                    text="Clear check-in"
                    color="red"
                    size="sm"
                    muted
                    disabled={submitting}
                    onClick={onClear}
                  />
                )}
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  text="Cancel"
                  color="grey"
                  size="sm"
                  muted
                  disabled={submitting}
                  onClick={onClose}
                />
                <Button
                  text={submitting ? "Saving..." : "Confirm"}
                  color="blue"
                  size="sm"
                  disabled={submitting || !valid}
                  onClick={() => onConfirm(date)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckinModal;
